// Mock of @dexmate/dexui to keep this workspace buildable until the real
// package is available. Components mirror the public API used by this
// frontend; styling is minimal Tailwind so the surface is recognizably
// the same shape as the real dexUI.
//
// When the real @dexmate/dexui ships, replace this directory with the
// upstream package or pnpm-workspace `link:` to it. NOT used in
// production builds — the dexhub-module externalizes @dexmate/dexui so
// the host's real implementation is used at runtime.

import type {
  ButtonHTMLAttributes,
  ChangeEvent,
  HTMLAttributes,
  InputHTMLAttributes,
  PropsWithChildren,
  ReactNode,
  Ref,
  TextareaHTMLAttributes,
} from 'react';
import { forwardRef } from 'react';
import { toast as sonnerToast, Toaster } from 'sonner';

export interface DexButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
}

export const DexButton = forwardRef<HTMLButtonElement, DexButtonProps>(function DexButton(
  { variant = 'primary', className = '', children, ...rest },
  ref,
) {
  const styles: Record<NonNullable<DexButtonProps['variant']>, string> = {
    primary:
      'bg-dex-accent text-white hover:bg-dex-accent-hover shadow-sm',
    secondary:
      'border border-dex-border bg-dex-bg-elevated text-dex-text-primary hover:bg-dex-bg-tertiary',
    ghost:
      'text-dex-accent hover:bg-dex-bg-tertiary',
    danger:
      'bg-dex-error text-white hover:opacity-90',
  };
  return (
    <button
      ref={ref}
      className={`inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dex-accent focus-visible:ring-offset-2 focus-visible:ring-offset-dex-bg-secondary ${styles[variant]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
});

// Native event-based onChange (matches react-hook-form's `register()` and the
// browser DOM signature). Callers that only need the string value should
// wrap: `onChange={(e) => setText(e.currentTarget.value)}`.
type BaseInputProps = {
  label?: string;
  multiline?: boolean;
  rows?: number;
  className?: string;
};

type NativeInputProps = InputHTMLAttributes<HTMLInputElement> & BaseInputProps;
type NativeTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & BaseInputProps;

type AnyInputProps = NativeInputProps &
  Omit<NativeTextareaProps, keyof NativeInputProps | 'onChange'> & {
    onChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  };

// Forward-ref so react-hook-form's `register()` works when consumers spread it.
export const DexInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, AnyInputProps>(
  function DexInput({ label, multiline, rows, className = '', onChange, ...rest }, ref) {
    const id = (rest as { id?: string }).id ?? rest.name;
    const cls = `border border-dex-border rounded px-2 py-1 w-full ${className}`;
    return (
      <label className="block space-y-1">
        {label && <span className="text-sm">{label}</span>}
        {multiline ? (
          <textarea
            ref={ref as Ref<HTMLTextAreaElement>}
            id={id}
            rows={rows ?? 3}
            className={cls}
            onChange={onChange}
            {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
          />
        ) : (
          <input
            ref={ref as Ref<HTMLInputElement>}
            id={id}
            className={cls}
            onChange={onChange}
            {...(rest as InputHTMLAttributes<HTMLInputElement>)}
          />
        )}
      </label>
    );
  },
);

export interface DexTableColumn<T extends Record<string, ReactNode>> {
  key: keyof T & string;
  label: string;
}

export interface DexTableProps<T extends Record<string, ReactNode>> {
  columns: ReadonlyArray<DexTableColumn<T>>;
  data: ReadonlyArray<T>;
}

export function DexTable<T extends Record<string, ReactNode>>({ columns, data }: DexTableProps<T>) {
  return (
    <table className="w-full text-left">
      <thead>
        <tr className="text-sm text-dex-text-secondary">
          {columns.map((c) => (
            <th key={c.key} className="px-3 py-2">{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, idx) => (
          <tr key={idx} className="border-b border-dex-border">
            {columns.map((c) => (
              <td key={c.key} className="px-3 py-2">{row[c.key]}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function DexCard({ className = '', children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`bg-dex-bg-elevated rounded-xl border border-dex-border shadow-[0_1px_2px_rgba(7,23,31,0.04)] ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

type Variant = 'h1' | 'h2' | 'h3' | 'body' | 'caption';
const VARIANT_CLASS: Record<Variant, string> = {
  h1: 'text-2xl font-semibold tracking-tight text-dex-text-primary',
  h2: 'text-xl font-semibold tracking-tight text-dex-text-primary',
  h3: 'text-base font-semibold text-dex-text-primary',
  body: 'text-sm text-dex-text-primary',
  caption: 'text-xs uppercase tracking-wider font-medium text-dex-text-tertiary',
};

export function DexTypography({
  variant = 'body',
  className = '',
  children,
  ...rest
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>> & { variant?: Variant }) {
  return (
    <div className={`${VARIANT_CLASS[variant]} ${className}`} {...rest}>
      {children}
    </div>
  );
}

export interface DexTooltipProps {
  content: ReactNode;
  children: ReactNode;
}

export function DexTooltip({ content, children }: DexTooltipProps) {
  return (
    <span title={typeof content === 'string' ? content : undefined}>{children}</span>
  );
}

// dexToast — minimal API mirroring dexhub's dexToast helper. Uses sonner
// under the hood. Matches the dexhub vocabulary so call-sites are portable.
export const dexToast = {
  success: (msg: string, opts?: { description?: string }) =>
    sonnerToast.success(msg, opts),
  error: (msg: string, opts?: { description?: string }) =>
    sonnerToast.error(msg, opts),
  info: (msg: string, opts?: { description?: string }) =>
    sonnerToast.info(msg, opts),
  warning: (msg: string, opts?: { description?: string }) =>
    sonnerToast.warning(msg, opts),
  message: (msg: string, opts?: { description?: string }) =>
    sonnerToast(msg, opts),
};

// DexToaster — the singleton mount point. Mount once in App.tsx (standalone
// build) or let dexhub provide its own (dexhub-module build).
export function DexToaster() {
  return (
    <Toaster
      position="bottom-right"
      richColors
      closeButton
      duration={4000}
      toastOptions={{
        classNames: {
          toast:
            'bg-dex-bg-elevated border-dex-border shadow-md rounded-lg text-sm',
          title: 'text-dex-text-primary font-medium',
          description: 'text-dex-text-tertiary',
        },
      }}
    />
  );
}

// DexNavItem — sidebar nav link. Mirrors dexhub/frontend/src/dexUI/DexNavItem.tsx
// active style (subtle blue overlay, semibold). Renders as an anchor when href
// is given, button otherwise. Accepts a Lucide icon as the icon prop.
export interface DexNavItemProps {
  icon?: ReactNode;
  label: string;
  href?: string;
  active?: boolean;
  badge?: number | string;
  onClick?: () => void;
  className?: string;
}

export function DexNavItem({
  icon,
  label,
  href,
  active = false,
  badge,
  onClick,
  className = '',
}: DexNavItemProps) {
  const cls = [
    'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dex-accent',
    'min-h-9 cursor-pointer w-full',
    active
      ? 'bg-dex-nav-active-bg text-dex-text-primary font-semibold'
      : 'text-dex-text-secondary font-normal hover:bg-dex-nav-hover-bg hover:text-dex-text-primary',
    className,
  ].join(' ');

  const inner = (
    <>
      {icon && (
        <span
          aria-hidden
          className={`shrink-0 inline-flex size-4 items-center justify-center [&>svg]:size-4 ${
            active ? 'text-dex-text-primary' : 'text-dex-text-tertiary'
          }`}
        >
          {icon}
        </span>
      )}
      <span className="flex-1 truncate">{label}</span>
      {badge !== undefined && (
        <span className="rounded-full bg-dex-status-info-bg px-1.5 py-0.5 text-[10px] font-semibold text-dex-status-info">
          {badge}
        </span>
      )}
    </>
  );

  if (href) {
    return (
      <a href={href} className={cls} aria-current={active ? 'page' : undefined}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}

// DexBreadcrumb — list of crumbs with chevron separators. Each crumb except
// the last is a link; the last (current page) is plain text.
export interface DexBreadcrumbCrumb {
  label: string;
  href?: string;
}
export interface DexBreadcrumbProps {
  items: ReadonlyArray<DexBreadcrumbCrumb>;
}

export function DexBreadcrumb({ items }: DexBreadcrumbProps) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs">
      <ol className="flex flex-wrap items-center gap-1">
        {items.map((c, i) => {
          const isLast = i === items.length - 1;
          return (
            <li key={`${c.label}-${i}`} className="flex items-center gap-1">
              {i > 0 && (
                <span aria-hidden className="text-dex-text-hint">
                  /
                </span>
              )}
              {isLast || !c.href ? (
                <span
                  className="text-dex-text-secondary"
                  aria-current={isLast ? 'page' : undefined}
                >
                  {c.label}
                </span>
              ) : (
                <a
                  href={c.href}
                  className="text-dex-text-tertiary hover:text-dex-text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dex-accent rounded"
                >
                  {c.label}
                </a>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export interface DexTagProps {
  tone?: 'success' | 'warning' | 'danger' | 'info' | 'neutral';
  children: ReactNode;
}

export function DexTag({ tone = 'neutral', children }: DexTagProps) {
  const tones: Record<NonNullable<DexTagProps['tone']>, string> = {
    success: 'bg-dex-status-success-bg text-dex-status-success',
    warning: 'bg-dex-status-warning-bg text-dex-status-warning',
    danger: 'bg-dex-status-error-bg text-dex-status-error',
    info: 'bg-dex-status-info-bg text-dex-status-info',
    neutral: 'bg-dex-status-neutral-bg text-dex-status-neutral',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone]}`}
    >
      {children}
    </span>
  );
}
